/**
 * Billing-owner resolution (org → Clerk sub partition for Stripe records).
 *
 * Stripe customer / payment-method records are keyed per Clerk user sub
 * (`stripe_customer_id:<sub>` / `stripe_pm_id:<sub>`). Managed orgs carry an
 * `org:<orgId>:billing_owner_sub` pointer so those per-sub records resolve from
 * an org_id alone. Shared by internal-wallet.ts (auto-top-up), stripe-webhook.ts
 * (PM persistence), and wallet-tools.ts (checkout customer reuse) so all three
 * use IDENTICAL resolution — keeping a single source of truth avoids drift.
 *
 * Resolution order:
 *   1. `org:<orgId>:billing_owner_sub` KV pointer (explicit billing owner)
 *   2. the org_id itself as the partition key (org-keyed billing records)
 */

import type { Env } from "./types.js";

export async function resolveBillingSub(env: Env, orgId: string): Promise<string> {
  // stdio mode stubs every CF binding to null (see apps/mcp-stdio/src/index.ts).
  // A chained `.catch()` cannot save us here: reading `.get` off null throws a
  // SYNCHRONOUS TypeError before any promise exists, so the guard has to come
  // first. Same shape already used by billing.ts and apps/app-state.ts.
  if (!env.CARRIER_USERS) return orgId;
  const pointer = await env.CARRIER_USERS.get(
    `org:${orgId}:billing_owner_sub`,
  ).catch(() => null);
  return pointer ?? orgId;
}
